import React, { memo, useMemo, useContext } from 'react';
import type { CSSProperties } from 'react';
import type {
  TapTarget,
  LocalizedLabel,
  LegalLinks as LegalLinksType,
  PaywallFooterStyle,
  StepNode,
} from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { OnActionCallback } from '../../../types/events';
import { mapPaywallFooterStyles } from '../../../utils/styleMapper';
import { Button } from '../Button';
import { Text } from '../Text';
import { LegalLinks, type LegalLinksEventContext } from '../LegalLinks';
import { useElementAnimation } from '../../../animations';
import { paywallFooterRenders } from '../../../utils/renderPredicates';
import styles from './PaywallFooter.module.css';
import { PurchaseSlotContext } from '@modules/screen-view-v2/purchaseSlot';

export interface PaywallFooterProps {
  purchaseAction?: TapTarget;
  selectedProductId?: string;
  autoRenewLabel?: LocalizedLabel;
  legalLinks?: LegalLinksType;
  footerAppearance?: PaywallFooterStyle;
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  onAction?: OnActionCallback;
  onLinkClick?: (context: LegalLinksEventContext) => void;
  isLoading?: boolean;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

export const PaywallFooter = memo(function PaywallFooter({
  purchaseAction,
  selectedProductId,
  autoRenewLabel,
  legalLinks,
  footerAppearance,
  step,
  locale,
  variables,
  onAction,
  onLinkClick,
  isLoading = false,
  className,
  'data-testid': testId = 'paywall-footer',
  editPath,
}: PaywallFooterProps): React.ReactElement | null {
  const renderSlot = useContext(PurchaseSlotContext);
  // ⚠️ Rules of Hooks: hooks first, the empty-footer early-return after —
  // footer members can materialize mid-session (theme/editor writes) and a
  // pre-hook return flips the hook count ("Rendered more hooks…" crash).
  const containerStyle: CSSProperties = useMemo(() => ({
    ...mapPaywallFooterStyles(footerAppearance),
  }), [footerAppearance]);
  // Screen entrance slot `paywall.footer` (F14 / AMV-AC-038) — last in the screen's order. The Paywall
  // screen passes editPath `content.paywall` (the ref is `paywall.footer`).
  const footerAnim = useElementAnimation(editPath ? `${editPath}.footer` : undefined);

  // Renders nothing without a purchase button, an auto-renew label or legal links (shared with the
  // entrance plan)
  if (!paywallFooterRenders(purchaseAction, autoRenewLabel, legalLinks)) {
    return null;
  }

  const elementsOrder = footerAppearance?.elementsOrder;
  const isPurchaseFirst = !elementsOrder || elementsOrder === 'PurchaseFirst';

  // Payment surface. (1) The entrance keeps no transform once it ends: the fill is forced to
  // `backwards` (the entrance default) — an authored `forwards`/`both` would leave a transform, i.e. a
  // containing block, on the purchase button's ancestor. (2) A provider purchase slot (the Paddle
  // in-page express frame, PurchaseSlotContext) mounts at the purchase position and measures its host
  // (getBoundingClientRect width) while the entrance would still run — a scale preset would shrink it
  // below the frame minimum. Then the footer element itself never animates: only its static parts
  // (auto-renew label, legal links) do, reading the timing vars the footer carries (inherited).
  const hostsProviderSlot = renderSlot !== null;
  const footerAnimStyle = footerAnim.className
    ? ({ ...footerAnim.style, '--sv2-anim-fill': 'backwards' } as CSSProperties)
    : undefined;
  const staticPartAnimClass = hostsProviderSlot ? footerAnim.className : undefined;
  const containerClassName = [
    styles.paywallFooter, className, hostsProviderSlot ? undefined : footerAnim.className,
  ].filter(Boolean).join(' ');

  const renderPurchase = () => {
    if (!purchaseAction) return null;
    const fallback = (
    <Button
      key="purchase"
      tapTarget={purchaseAction}
      locale={locale}
      variables={variables}
      step={step}
      onAction={onAction}
      isLoading={isLoading}
      data-testid={`${testId}-purchase`}
      editPath={editPath ? `${editPath}.purchaseAction` : undefined}
    />
    );
    return renderSlot ? renderSlot({ action: purchaseAction, step, productId: selectedProductId, source: 'paywall', fallback }) : fallback;
  };

  const renderAutoRenewLabel = () => autoRenewLabel && (
    <Text
      key="autoRenewLabel"
      label={autoRenewLabel}
      locale={locale}
      variables={variables}
      className={staticPartAnimClass ? `${styles.autoRenewLabel} ${staticPartAnimClass}` : styles.autoRenewLabel}
      data-testid={`${testId}-auto-renew`}
      editPath={editPath ? `${editPath}.autoRenewLabel` : undefined}
    />
  );

  const renderMainElements = () => {
    if (isPurchaseFirst) {
      return (
        <>
          {renderPurchase()}
          {renderAutoRenewLabel()}
        </>
      );
    }
    return (
      <>
        {renderAutoRenewLabel()}
        {renderPurchase()}
      </>
    );
  };

  return (
    <footer
      className={containerClassName}
      style={footerAnimStyle ? { ...containerStyle, ...footerAnimStyle } : containerStyle}
      data-anim={hostsProviderSlot ? undefined : footerAnim['data-anim']}
      data-testid={testId}
      data-edit-path={editPath}
    >
      {renderMainElements()}

      {legalLinks && (
        <LegalLinks
          legalLinks={legalLinks}
          locale={locale}
          variables={variables}
          onLinkClick={onLinkClick}
          className={staticPartAnimClass}
          data-testid={`${testId}-links`}
          editPath={editPath ? `${editPath}.legalLinks` : undefined}
        />
      )}
    </footer>
  );
});

PaywallFooter.displayName = 'PaywallFooter';
